import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ArchiveIcon,
  CreditCardIcon,
  DownloadIcon,
  EllipsisIcon,
  ExternalLinkIcon,
  FileTextIcon,
  MailIcon,
  PaperclipIcon,
  PencilIcon,
  PinIcon,
  PinOffIcon,
  PlusIcon,
  StickyNoteIcon,
  TruckIcon,
  XIcon,
} from 'lucide-react';
import { formatEventDate } from '@/lib/event-time';
import { legacyNoteHtml, NoteBody } from '@/components/NoteBody';
import { RichTextEditor } from '@/components/RichTextEditor';
import { ShippingPanel } from './event-shipping';
import { ClientShipmentsPanel } from './event-client-portal';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button, Combobox, ConfirmDialog, DataTable, DatePicker, Drawer, EmptyState, ErrorNotice, FactList, Field, LoadingState, Panel, Pill, SearchInput, SelectInput, TextArea, TextInput, type DataTableSort, type Fact, type PillTone } from '@bcecloud/portal-ui';
import { listPackingList } from '@/lib/supplies';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { errorMessage } from '@/lib/api';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_AUDIENCE,
  NOTE_CATEGORIES,
  SHIPMENT_CARRIERS,
  attachmentRejection,
  archiveNote,
  carrierLabel,
  shipmentStatusLabel,
  statusIsManual,
  piecesLabel,
  FREIGHT_CARRIERS,
  SHIPMENT_STATUSES,
  createEventNote,
  noteCategoryLabel,
  setNotePinned,
  listEventAttachments,
  listEventNotes,
  listEventShipments,
  createEventShipment,
  CLIENT_FEEDBACK_MAX_LENGTH,
  createRecap,
  deleteShipment,
  editsAnyRecap,
  listRecaps,
  readMyIdentity,
  restoreNote,
  updateEventNote,
  updateRecap,
  updateShipment,
  uploadEventAttachment,
  updateEventAttachment,
  deleteEventAttachment,
  type AttachmentAudience,
  type EventAttachment,
  type EventDetail,
  type EventNote,
  type EventRecap,
  type NoteCategory,
  type NoteWrite,
  type RecapAnswer,
  type Shipment,
  type ShipmentPatch,
  readEvent,
} from '@/lib/events';
import { listPeople } from '@/lib/staffing';
import {
  CONTACT_SOURCE_LABEL,
  contactChoiceValue,
  contactRefPath,
  contactValueFor,
  directoryChoices,
  eventContactChoices,
  withTypedPart,
} from '@/lib/contact-choices';
import {
  RECAP_DETAIL_SUFFIX,
  RECAP_QUESTIONS,
  conditionalFor,
} from '@/lib/recap-questions';
import { cn } from '@/lib/utils';
import { APP_FEATURE, useAppFeatures } from '@/lib/app-features';
import {
  formatTemplateValue,
  listTemplates,
  templateForCategory,
  templateStatusTone,
  type Template,
  type TemplateValue,
  type TemplateAddress,
  type TemplateContact,
  type TemplateField,
  type TemplateListItem,
  type ContactPart,
  ADDRESS_PARTS,
  contactRefOf,
} from '@/lib/templates';

/**
 * The remaining sections of one event: notes, shipments, attachments, recap.
 *
 * Split from EventDetailPage so each tab stays a small, testable unit — the
 * page owns the tab bar; these own their data.
 */

// ─── Notes ──────────────────────────────────────────────────────────────────

/**
 * The event's notes, grouped by category, with capture inline.
 *
 * The old app sent note-writing to a separate form page. Notes get written in
 * the field — between a venue walk and a client call — so capture lives on the
 * same screen as reading.
 *
 * @param props - The event id.
 * @returns The notes tab.
 */
/** The category filter the notes tab is currently showing. */
type NoteView = 'all' | 'archived' | NoteCategory;

/** Persist the last-used notes view so a preferred landing filter sticks. */
const NOTE_VIEW_KEY = 'bce.notes.view';

/** The author's display name from a note's owner/updatedBy/archivedBy ref. */
function noteAuthorName(ref: EventNote['owner']): string | undefined {
  if (typeof ref !== 'object' || ref === null) return undefined;
  return [ref.firstName, ref.lastName].filter(Boolean).join(' ') || ref.email;
}

export function NotesTab({ eventId }: { readonly eventId: string }): React.ReactElement {
  const queryClient = useQueryClient();
  // Legacy-parity modify rule: internal roles touch any note, contractors
  // only their own. The API enforces the same; hiding avoids dead buttons.
  const identity = useQuery({
    queryKey: ['my-identity'],
    queryFn: ({ signal }) => readMyIdentity(signal),
    staleTime: 5 * 60_000,
  });
  const internalViewer = ['internal', 'admin', 'superadmin'].includes(identity.data?.role ?? '');
  const canModify = (note: EventNote): boolean => {
    if (internalViewer) return true;
    const owner = typeof note.owner === 'object' ? note.owner?._id : note.owner;
    return owner !== undefined && owner === identity.data?.id;
  };
  const [composing, setComposing] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [archivingNote, setArchivingNote] = useState<{ id: string; subject: string } | null>(null);
  const [editingNote, setEditingNote] = useState<EventNote | null>(null);
  // Remembered across events so a preferred filter (e.g. always "All") sticks.
  const [view, setView] = useState<NoteView>(
    () => (localStorage.getItem(NOTE_VIEW_KEY) as NoteView | null) ?? 'all',
  );
  function selectView(next: NoteView): void {
    setView(next);
    localStorage.setItem(NOTE_VIEW_KEY, next);
  }

  const notes = useQuery({
    queryKey: ['event-notes', eventId],
    queryFn: ({ signal }) => listEventNotes(eventId, {}, signal),
  });
  // Templates label the structured values on each card; a missing one
  // (deleted since) falls back to the raw keys.
  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: ({ signal }) => listTemplates(signal),
    staleTime: 5 * 60_000,
  });
  const templateFor = (note: EventNote): Template | undefined =>
    note.templateKey ? templates.data?.find((template) => template.key === note.templateKey) : undefined;
  const archived = useQuery({
    queryKey: ['event-notes-archived', eventId],
    queryFn: ({ signal }) => listEventNotes(eventId, { showArchived: true }, signal),
    enabled: view === 'archived',
  });

  async function refresh(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['event-notes', eventId] }),
      queryClient.invalidateQueries({ queryKey: ['event-notes-archived', eventId] }),
    ]);
  }

  const archive = useMutation({
    mutationFn: (noteId: string) => archiveNote(noteId),
    onSuccess: async () => {
      setArchivingNote(null);
      await refresh();
    },
    onError: (err) => setError(errorMessage(err)),
  });
  const restore = useMutation({
    mutationFn: (noteId: string) => restoreNote(noteId),
    onSuccess: refresh,
    onError: (err) => setError(errorMessage(err)),
  });
  const pin = useMutation({
    mutationFn: (input: { noteId: string; pinned: boolean }) =>
      setNotePinned(input.noteId, input.pinned),
    onSuccess: refresh,
    onError: (err) => setError(errorMessage(err)),
  });

  if (notes.isPending) return <LoadingState label="Loading notes…" />;
  if (notes.isError) {
    return <ErrorNotice title="Notes unavailable" message={errorMessage(notes.error)} />;
  }

  const active = notes.data;
  // Categories that actually have notes, in the canonical order.
  const presentCategories = NOTE_CATEGORIES.filter((category) =>
    active.some((note) => (note.category ?? 'Basic') === category),
  );
  const archivedOnly = (archived.data ?? []).filter((note) => note.archivedAt);
  /** Pinned notes first (newest pin first); unpinned keep server order. */
  const pinnedFirst = (list: readonly EventNote[]): readonly EventNote[] =>
    [...list].sort(
      (a, b) =>
        (b.pinnedAt ? Date.parse(b.pinnedAt) : 0) - (a.pinnedAt ? Date.parse(a.pinnedAt) : 0),
    );
  const shown =
    view === 'all' || view === 'archived'
      ? active
      : pinnedFirst(active.filter((note) => (note.category ?? 'Basic') === view));

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        {/* Category filter row: All, each present category, then Archived. */}
        <div className="-mx-1 flex flex-1 flex-wrap gap-1">
          <FilterChip label="All" active={view === 'all'} onClick={() => selectView('all')} />
          {presentCategories.map((category) => (
            <FilterChip
              key={category}
              label={noteCategoryLabel(category)}
              active={view === category}
              onClick={() => selectView(category)}
            />
          ))}
          <FilterChip
            label="Archived"
            active={view === 'archived'}
            onClick={() => selectView('archived')}
          />
        </div>
        <Button guide="notes-new" onClick={() => setComposing(true)}>
          <PlusIcon aria-hidden className="size-4" /> New note
        </Button>
      </div>

      {error === undefined ? null : (
        <ErrorNotice inline title="Something went wrong" message={error} />
      )}

      {view === 'archived' ? (
        <ArchivedNotes
          notes={archivedOnly}
          loading={archived.isPending}
          onRestore={(id) => restore.mutate(id)}
          restoringId={restore.isPending ? restore.variables : undefined}
          mayRestore={canModify}
        />
      ) : active.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<StickyNoteIcon aria-hidden className="size-6" />}
            title="No notes yet"
            message="What you learn planning this event goes here."
            action={<Button onClick={() => setComposing(true)}>Write the first note</Button>}
          />
        </Panel>
      ) : view === 'all' ? (
        // Grouped by category — the "all in order" view. Pinned notes get
        // their own section on top (and still lead their own category).
        <div className="space-y-6">
          {active.some((note) => note.pinnedAt) ? (
            <div>
              <h3 className="mb-2 flex items-center gap-1.5 text-portal-micro font-extrabold tracking-[1px] text-brand uppercase">
                <PinIcon aria-hidden className="size-3.5" /> Pinned
              </h3>
              <div className="space-y-3">
                {pinnedFirst(active.filter((note) => note.pinnedAt)).map((note) => (
                  <NoteCard
                    key={note._id}
                    note={note}
                    template={templateFor(note)}
                    {...(canModify(note)
                      ? {
                          onArchive: () => setArchivingNote({ id: note._id, subject: note.subject }),
                          onTogglePin: () => pin.mutate({ noteId: note._id, pinned: !note.pinnedAt }),
                          onEdit: () => setEditingNote(note),
                        }
                      : {})}
                  />
                ))}
              </div>
            </div>
          ) : null}
          {presentCategories.map((category) => (
            <div key={category}>
              <h3 className="mb-2 text-portal-micro font-extrabold tracking-[1px] text-portal-muted uppercase">
                {noteCategoryLabel(category)}
              </h3>
              <div className="space-y-3">
                {pinnedFirst(
                  active.filter((note) => (note.category ?? 'Basic') === category),
                ).map((note) => (
                  <NoteCard
                    key={note._id}
                    note={note}
                    template={templateFor(note)}
                    {...(canModify(note)
                      ? {
                          onArchive: () => setArchivingNote({ id: note._id, subject: note.subject }),
                          onTogglePin: () => pin.mutate({ noteId: note._id, pinned: !note.pinnedAt }),
                          onEdit: () => setEditingNote(note),
                        }
                      : {})}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map((note) => (
            <NoteCard
              key={note._id}
              note={note}
                    template={templateFor(note)}
              {...(canModify(note)
                ? {
                    onEdit: () => setEditingNote(note),
                    onArchive: () => setArchivingNote({ id: note._id, subject: note.subject }),
                    onTogglePin: () => pin.mutate({ noteId: note._id, pinned: !note.pinnedAt }),
                  }
                : {})}
            />
          ))}
        </div>
      )}

      <NoteDialog
        eventId={eventId}
        open={composing}
        onClose={() => setComposing(false)}
        onSaved={async () => {
          setComposing(false);
          await refresh();
        }}
      />

      {/* Keyed by note so the form state reseeds per note being edited. */}
      {editingNote ? (
        <NoteDialog
          key={editingNote._id}
          eventId={eventId}
          existing={editingNote}
          open
          onClose={() => setEditingNote(null)}
          onSaved={async () => {
            setEditingNote(null);
            await refresh();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={archivingNote !== null}
        tone="danger"
        title="Archive this note?"
        message={
          archivingNote
            ? `"${archivingNote.subject}" will be hidden from the event. It can be restored later.`
            : ''
        }
        confirmLabel="Archive note"
        onConfirm={() => {
          if (archivingNote) archive.mutate(archivingNote.id);
        }}
        onCancel={() => {
          if (!archive.isPending) setArchivingNote(null);
        }}
        pending={archive.isPending}
        error={archive.isError ? errorMessage(archive.error) : undefined}
      />
    </div>
  );
}

/** A filter chip in the notes category row. */
function FilterChip({
  label,
  active,
  onClick,
}: {
  readonly label: string;
  readonly active: boolean;
  readonly onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-portal-pill px-2.5 py-1 text-portal-micro font-bold transition-colors',
        active ? 'bg-brand text-white' : 'text-portal-muted hover:bg-portal-bg',
      )}
    >
      {label}
    </button>
  );
}

/**
 * A card action: text label on large screens, icon-only below, always
 * tooltipped — one shape across note and shipment cards so rows read as a set.
 */
function CardAction({
  icon: Icon,
  label,
  tooltip,
  tone = 'default',
  onClick,
}: {
  readonly icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  readonly label: string;
  readonly tooltip: string;
  readonly tone?: 'default' | 'danger' | 'brand';
  readonly onClick: () => void;
}): React.ReactElement {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          aria-label={label}
          className={cn(
            'flex items-center gap-1 rounded-portal-button px-1.5 py-1 text-portal-micro font-bold transition-colors hover:bg-portal-bg',
            tone === 'danger' && 'text-rose-600 hover:text-rose-700',
            tone === 'brand' && 'text-brand',
            tone === 'default' && 'text-portal-muted hover:text-portal-ink',
          )}
        >
          <Icon aria-hidden className="size-4" />
          <span className="hidden lg:inline">{label}</span>
        </button>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

/** Field types whose value is a paragraph or a block, read in a wider column than a short fact. */
const LONG_FIELD_TYPES: ReadonlySet<string> = new Set(['longtext', 'list']);

/**
 * The structured values of a note as facts, in the template's field order,
 * with fields the template no longer has (or a deleted template) shown by
 * key. Short answers sit three across; paragraphs two across, so a long
 * value is not squeezed into a third of the card.
 */
function noteFacts(note: EventNote, template: Template | undefined): { readonly short: Fact[]; readonly long: Fact[] } {
  const values = note.values ?? {};
  const ordered = [
    ...(template?.fields.map((field) => field.key) ?? []),
    ...Object.keys(values).filter((key) => !template?.fields.some((field) => field.key === key)),
  ];
  const short: Fact[] = [];
  const long: Fact[] = [];
  for (const key of ordered) {
    const field = template?.fields.find((candidate) => candidate.key === key);
    const value = formatTemplateValue(field, values[key]);
    if (value === undefined) continue;
    const fact: Fact = { label: field?.label ?? key, value: field && LONG_FIELD_TYPES.has(field.type) ? <span className="whitespace-pre-line">{value}</span> : value };
    (field && LONG_FIELD_TYPES.has(field.type) ? long : short).push(fact);
  }
  return { short, long };
}

/**
 * One note card: category pill, subject, body, and an authored-by footer.
 * Action handlers are optional: absent means read-only — the legacy portal's
 * rule, where a contractor reads every note but modifies only their own.
 */
function NoteCard({
  note,
  template,
  onArchive,
  onTogglePin,
  onEdit,
}: {
  readonly note: EventNote;
  /** The template the note follows, for field labels; absent when none or deleted. */
  readonly template?: Template | undefined;
  readonly onArchive?: () => void;
  readonly onTogglePin?: () => void;
  readonly onEdit?: () => void;
}): React.ReactElement {
  const author = noteAuthorName(note.owner);
  const updatedByName = noteAuthorName(note.updatedBy);
  const created = note.createdAt ? new Date(note.createdAt).toLocaleDateString() : undefined;
  const updated = note.updatedAt ? new Date(note.updatedAt).toLocaleDateString() : undefined;
  const pinned = Boolean(note.pinnedAt);
  const facts = noteFacts(note, template);
  return (
    <Panel guide="note-card" className={cn('p-4', pinned && 'border-brand/40')}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="flex items-center gap-1.5">
            <Pill tone="brand">{noteCategoryLabel(note.category ?? 'Basic')}</Pill>
            {note.status ? <Pill tone={templateStatusTone(note.status)}>{note.status}</Pill> : null}
            {pinned ? <PinIcon aria-hidden className="size-3.5 text-brand" /> : null}
          </span>
          <h3 className="mt-2 text-portal-card-title font-bold">{note.subject}</h3>
        </div>
        <TooltipProvider delayDuration={100}>
          <span className="flex shrink-0 items-center gap-1">
            {onTogglePin ? (
              <CardAction
                icon={pinned ? PinOffIcon : PinIcon}
                label={pinned ? 'Unpin' : 'Pin'}
                tooltip={pinned ? 'Unpin this note' : 'Pin to the top of its section'}
                tone={pinned ? 'brand' : 'default'}
                onClick={onTogglePin}
              />
            ) : null}
            {onEdit ? (
              <CardAction
                icon={PencilIcon}
                label="Edit"
                tooltip="Edit this note"
                onClick={onEdit}
              />
            ) : null}
            {onArchive ? (
              <CardAction
                icon={ArchiveIcon}
                label="Archive"
                tooltip="Archive — hidden from the event, restorable later"
                tone="danger"
                onClick={onArchive}
              />
            ) : null}
          </span>
        </TooltipProvider>
      </div>
      {facts.short.length > 0 || facts.long.length > 0 ? (
        <div className="mt-3 space-y-3 rounded-portal-button bg-portal-bg p-3">
          {facts.short.length > 0 ? <FactList facts={facts.short} columns={3} /> : null}
          {facts.long.length > 0 ? <FactList facts={facts.long} columns={2} /> : null}
        </div>
      ) : null}
      <NoteBody body={note.body} />
      {author || created ? (
        <p className="mt-2 text-portal-micro text-portal-muted">
          {author ? `Created by ${author}` : 'Created'}
          {created ? ` on ${created}` : ''}
          {updatedByName && updated ? ` · Updated by ${updatedByName} on ${updated}` : ''}
        </p>
      ) : null}
    </Panel>
  );
}

/** The archived-notes list with per-note restore, mirroring legacy's table. */
function ArchivedNotes({
  notes,
  loading,
  onRestore,
  restoringId,
  mayRestore,
}: {
  readonly notes: readonly EventNote[];
  readonly loading: boolean;
  readonly onRestore: (id: string) => void;
  readonly restoringId: string | undefined;
  /** Per-note restore permission — the same own-note rule as editing. */
  readonly mayRestore: (note: EventNote) => boolean;
}): React.ReactElement {
  if (loading) return <LoadingState label="Loading archived notes…" />;
  if (notes.length === 0) {
    return (
      <Panel>
        <EmptyState
          icon={<StickyNoteIcon aria-hidden className="size-6" />}
          title="No archived notes"
          message="Notes you archive from the event show up here, ready to restore."
        />
      </Panel>
    );
  }
  return (
    <div className="space-y-2">
      {notes.map((note) => {
        const by = noteAuthorName(note.archivedBy);
        const at = note.archivedAt ? new Date(note.archivedAt).toLocaleDateString() : undefined;
        return (
          <div
            key={note._id}
            className="flex items-center justify-between gap-3 rounded-portal-button border border-portal-line bg-white px-4 py-2.5"
          >
            <span className="min-w-0">
              <span className="block truncate text-portal-small font-bold text-portal-ink">
                {note.subject}
              </span>
              <span className="block text-portal-micro text-portal-muted">
                {noteCategoryLabel(note.category ?? 'Basic')}
                {by || at ? ` · Archived${by ? ` by ${by}` : ''}${at ? ` on ${at}` : ''}` : ''}
              </span>
            </span>
            {mayRestore(note) ? (
              <Button
                variant="secondary"
                onClick={() => onRestore(note._id)}
                disabled={restoringId === note._id}
              >
                {restoringId === note._id ? 'Restoring…' : 'Restore'}
              </Button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The note composer, in a modal: category, subject, and a rich-text body —
 * the same formatting vocabulary the legacy editor had, so everything written
 * here renders through NoteBody's allowlist unchanged.
 *
 * @param props - Event id, open state, and close/saved handlers.
 * @returns The new-note dialog.
 */
function NoteDialog({
  eventId,
  existing,
  open,
  onClose,
  onSaved,
}: {
  readonly eventId: string;
  /** The note being edited; absent for a new note. */
  readonly existing?: EventNote;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSaved: () => Promise<void>;
}): React.ReactElement {
  const [category, setCategory] = useState<NoteCategory>(
    (existing?.category as NoteCategory | undefined) ?? 'Basic',
  );
  const [subject, setSubject] = useState(existing?.subject ?? '');
  const [bodyHtml, setBodyHtml] = useState(existing?.body ?? '');

  // Structured part. The category picks a template automatically until the
  // writer overrides it with the picker; an existing note keeps its own.
  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: ({ signal }) => listTemplates(signal),
    staleTime: 5 * 60_000,
  });
  const [templateKey, setTemplateKey] = useState(existing?.templateKey ?? '');
  const [templateChosen, setTemplateChosen] = useState(Boolean(existing?.templateKey));
  const [status, setStatus] = useState(existing?.status ?? '');
  const [values, setValues] = useState<Readonly<Record<string, TemplateValue>>>(existing?.values ?? {});
  // Note templates switched off: new notes are plain. A note that already
  // has a template keeps it, so editing it never drops its fields.
  const templatesOffered = useAppFeatures()(APP_FEATURE.noteTemplates) || Boolean(existing?.templateKey);
  const template = templateKey ? templates.data?.find((candidate) => candidate.key === templateKey) : undefined;
  const enabledTemplates = (templates.data ?? []).filter((candidate) => candidate.enabled || candidate.key === existing?.templateKey);

  // Choosing a template fills the subject with its default, unless the person
  // already wrote one. A default from the previous template counts as unwritten.
  function applyTemplate(nextKey: string): void {
    const previous = templates.data?.find((candidate) => candidate.key === templateKey);
    const next = templates.data?.find((candidate) => candidate.key === nextKey);
    setTemplateKey(nextKey);
    const untouched = subject.trim() === '' || (previous?.defaultSubject !== undefined && subject === previous.defaultSubject);
    if (untouched && next?.defaultSubject) setSubject(next.defaultSubject);
  }
  function selectCategory(next: NoteCategory): void {
    setCategory(next);
    if (!templateChosen && templatesOffered) applyTemplate(templateForCategory(templates.data ?? [], next)?.key ?? '');
  }
  function setValue(key: string, value: TemplateValue): void {
    setValues((current) => ({ ...current, [key]: value }));
  }

  /** The structured fields to send: only this template's keys, or a clear. */
  function structuredPart(): Pick<NoteWrite, 'templateKey' | 'status' | 'values'> {
    if (template) {
      const kept = Object.fromEntries(template.fields.map((field) => [field.key, values[field.key] ?? null]));
      return { templateKey: template.key, status: template.status.enabled && status ? status : null, values: kept };
    }
    return existing?.templateKey ? { templateKey: null, status: null, values: null } : {};
  }

  const save = useMutation({
    mutationFn: () => {
      const note: NoteWrite = { category, subject: subject.trim(), body: bodyHtml, ...structuredPart() };
      return existing ? updateEventNote(existing._id, note) : createEventNote(eventId, note);
    },
    onSuccess: async () => {
      if (!existing) {
        setSubject('');
        setBodyHtml('');
        setValues({});
        setStatus('');
      }
      save.reset();
      await onSaved();
    },
  });

  const requiredMissing = (template?.fields ?? []).some((field) => {
    const value = values[field.key];
    return field.required && (value === undefined || value === null || value === '' || value === false);
  });
  // With a template, the facts above can be the whole note; the log is
  // optional. Without one, the body is the note, so it has to be there.
  const bodyRequired = template === undefined;
  const canPost = subject.trim().length > 0 && (bodyHtml.length > 0 || !bodyRequired) && !requiredMissing && !save.isPending;

  return (
    <Dialog open={open} onOpenChange={(next) => (!next && !save.isPending ? onClose() : undefined)}>
      {/* preventOutsideClose: a half-written note must survive a stray
          backdrop click — only Cancel, the X, or Esc close this form. */}
      <DialogContent preventOutsideClose className="max-h-[calc(100vh-4rem)] overflow-y-auto sm:max-w-3xl">
        <DialogTitle>{existing ? 'Edit note' : 'New note'}</DialogTitle>
        <form
          className="mt-2 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (canPost) save.mutate();
          }}
        >
          <div className={cn('grid gap-4', templatesOffered ? 'sm:grid-cols-[180px_200px_1fr]' : 'sm:grid-cols-[180px_1fr]')}>
            <Field guide="note-category" label="Category" htmlFor="note-category">
              <SelectInput
                id="note-category"
                value={category}
                onChange={(event) => selectCategory(event.target.value as NoteCategory)}
              >
                {NOTE_CATEGORIES.map((option) => (
                  <option key={option} value={option}>
                    {noteCategoryLabel(option)}
                  </option>
                ))}
              </SelectInput>
            </Field>
            {templatesOffered ? (
              <Field guide="note-template" label="Template" htmlFor="note-template">
                <SelectInput
                  id="note-template"
                  value={templateKey}
                  onChange={(event) => {
                    applyTemplate(event.target.value);
                    setTemplateChosen(true);
                  }}
                >
                  <option value="">None</option>
                  {enabledTemplates.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.name}
                    </option>
                  ))}
                </SelectInput>
              </Field>
            ) : null}
            <Field guide="note-subject" label="Subject" htmlFor="note-subject" required>
              <TextInput
                id="note-subject"
                value={subject}
                maxLength={150}
                placeholder="What is this note about?"
                onChange={(event) => setSubject(event.target.value)}
              />
            </Field>
          </div>

          {template ? (
            <TemplateFields
              eventId={eventId}
              template={template}
              status={status}
              values={values}
              onStatus={setStatus}
              onValue={setValue}
            />
          ) : null}

          <Field
            guide="note-body"
            label={template?.logEnabled ? 'Log' : 'Note'}
            htmlFor="note-body"
            required={bodyRequired}
            {...(template?.logEnabled ? { hint: 'Dated lines, newest first — the running record under the facts above.' } : {})}
          >
            <RichTextEditor
              ariaLabel="Note body"
              // Legacy bodies are plain text with newline paragraph breaks;
              // TipTap parses HTML, so seed it with the same transform
              // NoteBody renders with or the breaks collapse.
              initialHtml={existing ? legacyNoteHtml(existing.body) : undefined}
              onChange={setBodyHtml}
            />
          </Field>

          {save.isError ? (
            <ErrorNotice inline title="Note not saved" message={errorMessage(save.error)} />
          ) : null}

          <div className="flex items-center justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canPost}>
              {save.isPending ? 'Saving…' : 'Save note'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The structured part of the note composer: the template's status and its
 * fields, each rendered by type. Values are controlled by the dialog.
 */
function TemplateFields({
  eventId,
  template,
  status,
  values,
  onStatus,
  onValue,
}: {
  /** The event the note is on; its contacts are offered first in contact fields. */
  readonly eventId: string;
  readonly template: Template;
  readonly status: string;
  readonly values: Readonly<Record<string, TemplateValue>>;
  readonly onStatus: (status: string) => void;
  readonly onValue: (key: string, value: TemplateValue) => void;
}): React.ReactElement {
  return (
    <div data-guide="note-fields" className="rounded-portal-button border border-portal-line bg-portal-bg p-4">
      <p className="text-portal-micro font-extrabold tracking-[1px] text-portal-muted uppercase">{template.name}</p>
      {template.description ? <p className="mt-1 text-portal-micro text-portal-muted">{template.description}</p> : null}
      <div className="mt-3 grid gap-4 sm:grid-cols-2">
        {template.status.enabled ? (
          <Field guide="note-status" label="Status" htmlFor="note-status">
            <SelectInput id="note-status" value={status} onChange={(event) => onStatus(event.target.value)}>
              <option value="">No status yet</option>
              {template.status.options.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </SelectInput>
          </Field>
        ) : null}
        {template.fields.map((field) => (
          <FieldInput key={field.key} eventId={eventId} field={field} id={`note-field-${field.key}`} value={values[field.key]} onChange={(next) => onValue(field.key, next)} />
        ))}
      </div>
    </div>
  );
}

/** Whether a field takes the whole row of the two-column grid. */
function spansRow(field: TemplateField): boolean {
  return field.type === 'longtext' || field.type === 'address' || field.type === 'contact' || field.type === 'list';
}

/**
 * One template field as an input, by type. Used for the template's own
 * fields and for each field of a list item, so the two never drift.
 */
function FieldInput({
  eventId,
  field,
  id,
  value,
  onChange,
}: {
  readonly eventId: string;
  readonly field: TemplateField;
  readonly id: string;
  readonly value: TemplateValue | undefined;
  readonly onChange: (value: TemplateValue) => void;
}): React.ReactElement {
  const text = typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  const wrap = (node: React.ReactNode): React.ReactElement => (spansRow(field) ? <div className="sm:col-span-2">{node}</div> : <>{node}</>);
  const fieldProps = { label: field.label, htmlFor: id, required: field.required === true, ...(field.hint ? { hint: field.hint } : {}) };
  switch (field.type) {
    case 'bool':
      return (
        <div className="self-end pb-2">
          <label className="flex items-center gap-2 text-portal-small">
            <input type="checkbox" checked={value === true} onChange={(event) => onChange(event.target.checked)} />
            {field.required ? `${field.label} *` : field.label}
          </label>
          {field.hint ? <p className="mt-1 text-portal-micro text-portal-muted">{field.hint}</p> : null}
        </div>
      );
    case 'select':
      return (
        <Field {...fieldProps}>
          <SelectInput id={id} value={text} onChange={(event) => onChange(event.target.value)}>
            <option value="">—</option>
            {(field.options ?? []).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </SelectInput>
        </Field>
      );
    case 'longtext':
      return wrap(
        <Field {...fieldProps}>
          <TextArea id={id} rows={3} value={text} onChange={(event) => onChange(event.target.value)} />
        </Field>,
      );
    case 'datetime':
      return (
        <Field {...fieldProps}>
          <TextInput id={id} type="datetime-local" value={text} onChange={(event) => onChange(event.target.value)} />
        </Field>
      );
    case 'phone':
    case 'url':
    case 'text':
      return (
        <Field {...fieldProps}>
          <TextInput
            id={id}
            type={field.type === 'phone' ? 'tel' : field.type === 'url' ? 'url' : 'text'}
            value={text}
            onChange={(event) => onChange(event.target.value)}
          />
        </Field>
      );
    case 'address':
      return wrap(<AddressField field={field} id={id} value={value} onChange={onChange} />);
    case 'contact':
      return wrap(<ContactField field={field} id={id} eventId={eventId} value={value} onChange={onChange} />);
    case 'list':
      return wrap(<ListField eventId={eventId} field={field} id={id} value={value} onChange={onChange} />);
    default: {
      const exhaustive: never = field.type;
      return <span key={String(exhaustive)} />;
    }
  }
}

/**
 * A list field: one to many items, each shaped by the field's item fields.
 * Items are edited in place and removed one at a time.
 */
function ListField({ eventId, field, id, value, onChange }: { readonly eventId: string; readonly field: TemplateField; readonly id: string; readonly value: TemplateValue | undefined; readonly onChange: (next: readonly TemplateListItem[]) => void }): React.ReactElement {
  const items: readonly TemplateListItem[] = Array.isArray(value) ? (value as readonly TemplateListItem[]) : [];
  const itemFields = (field.itemFields ?? []).filter((item) => item.type !== 'list');
  const setItem = (index: number, key: string, next: TemplateValue): void => {
    onChange(items.map((item, i) => (i === index ? { ...item, [key]: next as TemplateListItem[string] } : item)));
  };
  return (
    <fieldset>
      <div className="mb-2 flex items-start justify-between gap-3">
        <CompositeHeading field={field} />
        <Button type="button" variant="secondary" size="compact" onClick={() => onChange([...items, {}])}>
          <PlusIcon aria-hidden className="size-4" /> Add item
        </Button>
      </div>
      {items.length === 0 ? (
        <EmptyState title="No items yet" message="Add the first item; each one takes the fields the template set." action={<Button type="button" variant="secondary" onClick={() => onChange([...items, {}])}>Add item</Button>} />
      ) : (
        <div className="space-y-3">
          {items.map((item, index) => (
            // Items have no identity of their own; the position is the key.
            // eslint-disable-next-line react/no-array-index-key
            <div key={index} className="rounded-portal-button border border-portal-line bg-white p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-portal-micro font-extrabold tracking-[1px] text-portal-muted uppercase">Item {index + 1}</span>
                <button
                  type="button"
                  aria-label={`Remove item ${index + 1}`}
                  onClick={() => onChange(items.filter((_row, i) => i !== index))}
                  className="grid size-7 place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-rose-600"
                >
                  <XIcon aria-hidden className="size-4" />
                </button>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {itemFields.map((itemField) => (
                  <FieldInput
                    key={itemField.key}
                    eventId={eventId}
                    field={itemField}
                    id={`${id}-${index}-${itemField.key}`}
                    value={item[itemField.key]}
                    onChange={(next) => setItem(index, itemField.key, next)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </fieldset>
  );
}

/** The parts of a composite value that are strings; anything else reads as empty. */
function partsOf(value: TemplateValue | undefined): Readonly<Record<string, string>> {
  if (typeof value !== 'object' || value === null) return {};
  return Object.fromEntries(Object.entries(value).filter((entry): entry is [string, string] => typeof entry[1] === 'string'));
}

/** A template field's label row: the label, the required mark and the hint, for a group of inputs. */
function CompositeHeading({ field }: { readonly field: TemplateField }): React.ReactElement {
  return (
    <div className="mb-2">
      <span className="block text-portal-tiny font-bold">
        {field.label}
        {field.required ? <span className="ml-0.5 text-rose-600" aria-hidden="true">*</span> : null}
      </span>
      {field.hint ? <p className="mt-0.5 text-portal-micro text-portal-muted">{field.hint}</p> : null}
    </div>
  );
}

/** An address as one field: street, city, state and postal code. */
function AddressField({ field, id: baseId, value, onChange }: { readonly field: TemplateField; readonly id: string; readonly value: TemplateValue | undefined; readonly onChange: (next: TemplateAddress) => void }): React.ReactElement {
  const parts = partsOf(value);
  const set = (part: (typeof ADDRESS_PARTS)[number], text: string): void => onChange({ ...parts, [part]: text });
  const id = (part: string): string => `${baseId}-${part}`;
  return (
    <fieldset>
      <CompositeHeading field={field} />
      <div className="space-y-3">
        <Field label="Street" htmlFor={id('street')}>
          <TextInput id={id('street')} value={parts['street'] ?? ''} onChange={(event) => set('street', event.target.value)} />
        </Field>
        <Field label="Street 2" htmlFor={id('street2')} hint="Suite, floor, dock or building, if any.">
          <TextInput id={id('street2')} value={parts['street2'] ?? ''} onChange={(event) => set('street2', event.target.value)} />
        </Field>
      <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr]">
        <Field label="City" htmlFor={id('city')}>
          <TextInput id={id('city')} value={parts['city'] ?? ''} onChange={(event) => set('city', event.target.value)} />
        </Field>
        <Field label="State" htmlFor={id('state')}>
          <TextInput id={id('state')} value={parts['state'] ?? ''} onChange={(event) => set('state', event.target.value)} />
        </Field>
        <Field label="ZIP" htmlFor={id('postalCode')}>
          <TextInput id={id('postalCode')} value={parts['postalCode'] ?? ''} onChange={(event) => set('postalCode', event.target.value)} />
        </Field>
      </div>
      </div>
    </fieldset>
  );
}

/**
 * A contact as one field: pick someone from this event's contacts or the
 * staff directory, which fills the parts and keeps a reference to who it
 * was, or type a new one by hand. The parts are what the note reads, so it
 * stays right after the contact changes or is removed.
 */
function ContactField({ field, id: baseId, eventId, value, onChange }: { readonly field: TemplateField; readonly id: string; readonly eventId: string; readonly value: TemplateValue | undefined; readonly onChange: (next: TemplateContact) => void }): React.ReactElement {
  const parts = partsOf(value);
  const current: TemplateContact = parts;
  const linked = contactRefOf(value);
  // The event is already loaded by the page; the directory is shared with the assistants picker.
  const event = useQuery({ queryKey: ['event', eventId], queryFn: ({ signal }) => readEvent(eventId, signal), staleTime: 60_000 });
  const people = useQuery({ queryKey: ['people'], queryFn: ({ signal }) => listPeople(signal), staleTime: 60_000 });
  const fromEvent = event.data ? eventContactChoices(event.data) : [];
  const choices = [...fromEvent, ...directoryChoices(people.data ?? [], fromEvent)];
  const set = (part: ContactPart, text: string): void => onChange(withTypedPart(current, part, text));
  const id = (part: string): string => `${baseId}-${part}`;
  const linkedPath = linked ? contactRefPath(linked) : undefined;
  return (
    <fieldset>
      <CompositeHeading field={field} />
      <div className="mb-3">
        <Field guide="note-contact-pick" label="Pick a contact" htmlFor={id('pick')} hint="This event's contacts come first, then staff contacts and the BCE team. Picking someone fills in the details below.">
          <Combobox
            id={id('pick')}
            options={choices}
            value={linked ? contactChoiceValue(linked) : ''}
            noneLabel="Type a new contact"
            placeholder={event.isPending || people.isPending ? 'Loading contacts…' : 'Search by name'}
            onChange={(next) => {
              const picked = choices.find((entry) => entry.value === next);
              if (picked) onChange(contactValueFor(picked));
              // "Type a new contact" keeps what is there and drops the link.
              else if (linked) onChange(withTypedPart(current, 'name', parts['name'] ?? ''));
            }}
          />
        </Field>
        {linked ? (
          <p className="mt-1 text-portal-micro text-portal-muted">
            Picked from {CONTACT_SOURCE_LABEL[linked.kind]}
            {linkedPath ? (
              <>
                :{' '}
                <Link to={linkedPath} className="font-bold text-primary hover:underline">
                  {parts['name'] || 'their record'}
                </Link>
              </>
            ) : null}
            . Editing a detail below makes it a typed contact.
          </p>
        ) : null}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name" htmlFor={id('name')}>
          <TextInput id={id('name')} value={parts['name'] ?? ''} onChange={(changed) => set('name', changed.target.value)} />
        </Field>
        <Field label="Company" htmlFor={id('company')}>
          <TextInput id={id('company')} value={parts['company'] ?? ''} onChange={(changed) => set('company', changed.target.value)} />
        </Field>
        <Field label="Email" htmlFor={id('email')}>
          <TextInput id={id('email')} type="email" value={parts['email'] ?? ''} onChange={(changed) => set('email', changed.target.value)} />
        </Field>
        <Field label="Phone" htmlFor={id('phone')}>
          <TextInput id={id('phone')} value={parts['phone'] ?? ''} onChange={(changed) => set('phone', changed.target.value)} />
        </Field>
      </div>
    </fieldset>
  );
}

// ─── Shipments ──────────────────────────────────────────────────────────────

/** Shippo statuses mapped to a triage tone. */
/** Sortable columns of the event's shipments table; keys are flattened row fields. */
const EVENT_SHIPMENT_SORT_FIELDS = {
  carrier: 'carrierName',
  vendor: 'vendor',
  status: 'statusKey',
  eta: 'etaMs',
} as const;
const EVENT_SHIPMENT_SORT_KEYS = Object.values(EVENT_SHIPMENT_SORT_FIELDS);
const DEFAULT_EVENT_SHIPMENT_SORT: DataTableSort = { key: EVENT_SHIPMENT_SORT_FIELDS.eta, direction: 'asc' };

/** A shipment plus the flattened fields the table sorts and searches on. */
interface EventShipmentRow extends Shipment {
  readonly carrierName: string;
  readonly statusKey: string;
  readonly etaMs: number | null;
  readonly etaIsDay: boolean;
  readonly itemsText: string;
  readonly toText: string;
}

function shipmentRow(shipment: Shipment): EventShipmentRow {
  const eta = shipment.eta ?? shipment.expectedArrivalDate;
  const etaMs = eta ? new Date(eta).getTime() : NaN;
  return {
    ...shipment,
    carrierName: carrierLabel(shipment.carrier),
    statusKey: shipment.trackingStatus?.status ?? shipment.status ?? 'UNKNOWN',
    etaMs: Number.isNaN(etaMs) ? null : etaMs,
    // A hand-entered arrival is a calendar day stored at UTC midnight; a
    // carrier ETA is a real instant. Only the former must render in UTC.
    etaIsDay: !shipment.eta && Boolean(shipment.expectedArrivalDate),
    itemsText: (shipment.items ?? [])
      .filter((item) => item.name)
      .map((item) => `${item.quantity ? `${item.quantity}× ` : ''}${item.name}`)
      .join(', '),
    toText: [shipment.addressTo?.name, shipment.addressTo?.city, shipment.addressTo?.state].filter(Boolean).join(', '),
  };
}

export const SHIPMENT_TONES: Readonly<Record<string, PillTone>> = {
  DELIVERED: 'success',
  TRANSIT: 'brand',
  PRE_TRANSIT: 'neutral',
  RETURNED: 'danger',
  FAILURE: 'danger',
  UNKNOWN: 'neutral',
};


/**
 * Shipments headed to (and from) this event, with booking and management for
 * BCE staff — the API gates writes to internal roles, so the controls only
 * render for them (anyone else would just collect 403s).
 *
 * @param props - The event id.
 * @returns The shipments tab.
 */
export function ShipmentsTab({
  eventId,
  clientPortal = false,
}: {
  readonly eventId: string;
  /** The client is on the portal: their receiving plan and receipts sit under the shipments. */
  readonly clientPortal?: boolean;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editingShipment, setEditingShipment] = useState<Shipment | null>(null);
  const [removingShipment, setRemovingShipment] = useState<{ id: string; label: string } | null>(
    null,
  );

  const me = useQuery({ queryKey: ['staff-me'], queryFn: ({ signal }) => readMyIdentity(signal) });
  const canManage = ['internal', 'admin', 'superadmin'].includes(me.data?.role ?? '');
  const returnLabelsOn = useAppFeatures()(APP_FEATURE.returnLabels);
  const [search, setSearch] = useState('');
  const { sort, onSort } = useTableSort(EVENT_SHIPMENT_SORT_KEYS, DEFAULT_EVENT_SHIPMENT_SORT);

  const shipments = useQuery({
    queryKey: ['event-shipments', eventId],
    queryFn: ({ signal }) => listEventShipments(eventId, signal),
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['event-shipments', eventId] });
  };
  const remove = useMutation({
    mutationFn: (shipmentId: string) => deleteShipment(shipmentId),
    onSuccess: async () => {
      setRemovingShipment(null);
      await refresh();
    },
  });

  if (shipments.isPending) return <LoadingState label="Loading shipments…" />;
  if (shipments.isError) {
    return <ErrorNotice title="Shipments unavailable" message={errorMessage(shipments.error)} />;
  }

  const newShipmentButton = (
    <span data-guide="shipments-new">
      <Button onClick={() => setCreating(true)}>
        <PlusIcon aria-hidden className="size-4" /> New shipment
      </Button>
    </span>
  );

  const rows: EventShipmentRow[] = sortRows(
    filterRows(shipments.data.map(shipmentRow), search, ['carrierName', 'trackingNumber', 'vendor', 'itemsText', 'toText']),
    sort,
  );
  const unusedReturnLabels = returnLabelsOn ? shipments.data.filter((shipment) => shipment.returnLabelUnused === true).length : 0;

  return (
    <div className="space-y-5">
      {/* Where materials ship and what the dock needs — the facts the
          tracking sheet's Shipping Label column carried. */}
      <div data-guide="shipping-brief">
        <ShippingPanel eventId={eventId} canEdit={canManage} />
      </div>
      {clientPortal ? <ClientShipmentsPanel eventId={eventId} /> : null}
      <div>
      {shipments.data.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<TruckIcon aria-hidden className="size-6" />}
            title="No shipments yet"
            message="Shipments to and from this event appear here with live carrier tracking as they are booked."
            action={canManage ? newShipmentButton : undefined}
          />
        </Panel>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <SearchInput
              data-guide="shipments-search"
              label="Search shipments"
              placeholder="Carrier, tracking #, vendor, item or destination"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="w-full max-w-sm"
            />
            {canManage ? (
              <div className="flex items-center gap-2">
                <Link
                  data-guide="shipments-email"
                  to={`/events/${eventId}/shipments/email`}
                  className="inline-flex items-center gap-2 rounded-portal-button border border-[#cad3df] bg-white px-4 py-2 text-portal-small font-bold hover:bg-portal-bg"
                >
                  <MailIcon aria-hidden className="size-4" /> Email client
                </Link>
                {newShipmentButton}
              </div>
            ) : null}
          </div>
          {unusedReturnLabels > 0 ? (
            <ErrorNotice
              inline
              tone="warning"
              message={
                unusedReturnLabels === 1
                  ? 'A return label has not been used: no carrier has scanned it a week after the event, so the materials may still be at the venue.'
                  : `${unusedReturnLabels} return labels have not been used: no carrier has scanned them a week after the event, so the materials may still be at the venue.`
              }
            />
          ) : null}
          {rows.length === 0 ? (
            <Panel>
              <EmptyState message="No shipments match that search." />
            </Panel>
          ) : (
            <div data-guide="shipments-table"><DataTable
              headers={[
                { label: 'Carrier & tracking', sortKey: EVENT_SHIPMENT_SORT_FIELDS.carrier },
                { label: 'Vendor', sortKey: EVENT_SHIPMENT_SORT_FIELDS.vendor },
                'Items',
                'To',
                { label: 'Status', sortKey: EVENT_SHIPMENT_SORT_FIELDS.status },
                { label: 'ETA', sortKey: EVENT_SHIPMENT_SORT_FIELDS.eta },
                'Client',
                'Actions',
              ]}
              visuallyHiddenHeaders={[7]}
              sort={sort}
              onSort={onSort}
              minWidth={900}
            >
              {rows.map((shipment) => (
                <tr key={shipment._id} className="border-t border-portal-line align-middle">
                  <td className="p-3 text-portal-small">
                    <span className="block text-portal-small font-bold">{shipment.carrierName}</span>
                    <Link
                      to={`/shipments/${shipment._id}`}
                      className="block font-mono text-portal-micro text-brand hover:underline"
                    >
                      {shipment.trackingNumber || shipment.proNumber || 'View shipment'}
                    </Link>
                    {shipment.proNumber && shipment.trackingNumber ? (
                      <span className="block font-mono text-portal-micro text-portal-muted">PRO {shipment.proNumber}</span>
                    ) : null}
                    {returnLabelsOn && shipment.returnLabel ? (
                      <span className="mt-1 block">
                        <Pill tone="neutral">Return label</Pill>
                      </span>
                    ) : null}
                  </td>
                  <td className="p-3 text-portal-small text-portal-muted">{shipment.vendor || '—'}</td>
                  <td className="p-3 text-portal-small text-portal-muted">
                    {shipment.itemsText || '—'}
                    {piecesLabel(shipment) ? <span className="block text-portal-micro">{piecesLabel(shipment)}</span> : null}
                  </td>
                  <td className="p-3 text-portal-small text-portal-muted">{shipment.toText || '—'}</td>
                  <td className="p-3 text-portal-small whitespace-nowrap">
                    <Pill tone={SHIPMENT_TONES[shipment.statusKey] ?? 'neutral'}>{shipmentStatusLabel(shipment.statusKey)}</Pill>
                    {returnLabelsOn && shipment.returnLabelUnused ? (
                      <span className="ml-1.5">
                        <Pill tone="danger">Unused</Pill>
                      </span>
                    ) : null}
                    {shipment.trackingStatus?.statusDetails ? (
                      <span className="mt-0.5 block max-w-[220px] truncate text-portal-micro text-portal-muted" title={shipment.trackingStatus.statusDetails}>
                        {shipment.trackingStatus.statusDetails}
                      </span>
                    ) : null}
                  </td>
                  <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">
                    {shipment.etaMs !== null
                      ? new Date(shipment.etaMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', ...(shipment.etaIsDay ? { timeZone: 'UTC' } : {}) })
                      : '—'}
                  </td>
                  {/* A return label never reaches the client's portal, whatever the switch says. */}
                  <td className="p-3 text-portal-small text-portal-muted">{shipment.viewableByClient === false || shipment.returnLabel ? '—' : 'Yes'}</td>
                  <td className="p-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          data-guide="shipments-row-menu" aria-label={`Actions for ${shipment.carrierName} ${shipment.trackingNumber ?? ''}`.trim()}
                          className="grid size-7 place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-portal-ink"
                        >
                          <EllipsisIcon aria-hidden className="size-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {canManage ? (
                          <DropdownMenuItem onSelect={() => setEditingShipment(shipment)}>
                            <PencilIcon aria-hidden className="size-4" />
                            Edit
                          </DropdownMenuItem>
                        ) : null}
                        <DropdownMenuItem asChild>
                          <Link to={`/shipments/${shipment._id}`}>
                            <TruckIcon aria-hidden className="size-4" />
                            Tracking history
                          </Link>
                        </DropdownMenuItem>
                        {shipment.link ? (
                          <DropdownMenuItem asChild>
                            <a href={shipment.link} target="_blank" rel="noreferrer">
                              <ExternalLinkIcon aria-hidden className="size-4" />
                              Track with the carrier
                            </a>
                          </DropdownMenuItem>
                        ) : null}
                        {canManage ? (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              variant="destructive"
                              onSelect={() =>
                                setRemovingShipment({
                                  id: shipment._id,
                                  label: [shipment.carrierName, shipment.trackingNumber].filter(Boolean).join(' '),
                                })
                              }
                            >
                              <XIcon aria-hidden className="size-4" />
                              Remove
                            </DropdownMenuItem>
                          </>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </DataTable></div>
          )}
        </>
      )}


      <ShipmentDialog
        eventId={eventId}
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={async () => {
          setCreating(false);
          await refresh();
        }}
      />

      {/* Keyed by shipment so the form state reseeds per shipment edited. */}
      {editingShipment ? (
        <ShipmentDialog
          key={editingShipment._id}
          eventId={eventId}
          existing={editingShipment}
          open
          onClose={() => setEditingShipment(null)}
          onSaved={async () => {
            setEditingShipment(null);
            await refresh();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={removingShipment !== null}
        title="Remove this shipment?"
        message={
          removingShipment
            ? `${removingShipment.label || 'This shipment'} will be removed from the event. This cannot be undone.`
            : ''
        }
        confirmLabel="Remove"
        tone="danger"
        pending={remove.isPending}
        onConfirm={() => {
          if (removingShipment) remove.mutate(removingShipment.id);
        }}
        onCancel={() => setRemovingShipment(null)}
      />
      </div>
    </div>
  );
}


/**
 * The create/edit shipment form — carrier, tracking, vendor, arrival,
 * client visibility and the item lines, matching the V3 write schema.
 *
 * @param props - The event, the shipment being edited (absent for create),
 *   and open/close/saved callbacks.
 * @returns The dialog.
 */
export function ShipmentDialog({
  eventId,
  existing,
  open,
  onClose,
  onSaved,
}: {
  readonly eventId: string;
  /** The shipment being edited; absent for a new one. */
  readonly existing?: Shipment;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSaved: () => Promise<void>;
}): React.ReactElement {
  const [carrier, setCarrier] = useState(existing?.carrier ?? 'fedex');
  const [trackingNumber, setTrackingNumber] = useState(existing?.trackingNumber ?? '');
  const [vendor, setVendor] = useState(existing?.vendor ?? '');
  const [arrival, setArrival] = useState(
    (existing?.expectedArrivalDate ?? existing?.eta ?? '').slice(0, 10),
  );
  const [viewableByClient, setViewableByClient] = useState(existing?.viewableByClient ?? true);
  const [returnLabel, setReturnLabel] = useState(existing?.returnLabel ?? false);
  const [labelText, setLabelText] = useState(existing?.labelText ?? '');
  const [proNumber, setProNumber] = useState(existing?.proNumber ?? '');
  const [pieceCount, setPieceCount] = useState(existing?.pieces?.count !== undefined ? String(existing.pieces.count) : '');
  const [pieceOf, setPieceOf] = useState(existing?.pieces?.of !== undefined ? String(existing.pieces.of) : '');
  const [pieceType, setPieceType] = useState<'box' | 'pallet'>(existing?.pieceType ?? 'box');
  const [status, setStatus] = useState(existing?.status ?? 'PRE_TRANSIT');
  // An item is either typed by hand or a line from the event's Materials tab;
  // a linked line lists this shipment in return, so the two tabs agree.
  const [items, setItems] = useState<readonly { packingLineId: string; name: string; quantity: string }[]>(
    (existing?.items ?? []).map((item) => ({
      packingLineId: item.packingLineId ?? '',
      name: item.name ?? '',
      quantity: String(item.quantity ?? ''),
    })),
  );
  // With Materials switched off there is no packing list to pick from; items are typed by hand.
  const isFeatureOn = useAppFeatures();
  const materialsOn = isFeatureOn(APP_FEATURE.materials);
  const returnLabelsOn = isFeatureOn(APP_FEATURE.returnLabels);
  const packing = useQuery({ queryKey: ['packing-list', eventId], queryFn: ({ signal }) => listPackingList(eventId, signal), enabled: open && materialsOn });
  const packingLines = packing.data ?? [];

  const setItem = (index: number, patch: Partial<{ packingLineId: string; name: string; quantity: string }>): void => {
    setItems((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };
  /** Linking a line takes its name and quantity; unlinking keeps what is typed. */
  const linkItem = (index: number, packingLineId: string): void => {
    const line = packingLines.find((candidate) => candidate._id === packingLineId);
    setItem(index, line ? { packingLineId, name: line.name, quantity: String(line.quantity) } : { packingLineId: '' });
  };

  // Legacy shipments free-typed the carrier, so an unrecognised one is added to
  // the list rather than dropped — otherwise saving any other edit would
  // silently rewrite the carrier to whichever option renders first.
  const knownCarriers = Object.keys(SHIPMENT_CARRIERS);
  const carrierOptions = knownCarriers.includes(carrier)
    ? knownCarriers
    : [carrier, ...knownCarriers];

  const save = useMutation({
    mutationFn: () => {
      const body: ShipmentPatch = {
        carrier,
        trackingNumber: trackingNumber.trim(),
        proNumber: proNumber.trim(),
        // Nothing entered means no pieces: omitted on create, cleared on edit.
        ...(pieceCount || pieceOf
          ? { pieces: { ...(pieceCount ? { count: Number(pieceCount) } : {}), ...(pieceOf ? { of: Number(pieceOf) } : {}) } }
          : existing
            ? { pieces: null }
            : {}),
        pieceType,
        ...(statusIsManual(carrier) ? { status } : {}),
        vendor: vendor.trim(),
        viewableByClient,
        // Switched off, the form leaves the flag as it was rather than clearing it.
        ...(returnLabelsOn ? { returnLabel } : {}),
        labelText: labelText.trim(),
        items: items
          .filter((item) => item.name.trim().length > 0)
          .map((item) => ({ name: item.name.trim(), quantity: Number(item.quantity) || 0, ...(item.packingLineId ? { packingLineId: item.packingLineId } : {}) })),
        ...(arrival ? { expectedArrivalDate: arrival } : {}),
      };
      return existing ? updateShipment(existing._id, body) : createEventShipment(eventId, body);
    },
    onSuccess: async () => {
      save.reset();
      await onSaved();
    },
  });

  // Parcel carriers need a tracking number for the feed to have anything to
  // read; freight tracks by PRO, so the tracking number stays optional there.
  const canSave =
    carrier.trim().length > 0 &&
    vendor.trim().length > 0 &&
    (FREIGHT_CARRIERS.has(carrier) || trackingNumber.trim().length > 0) &&
    !save.isPending;

  return (
    <Dialog open={open} onOpenChange={(next) => (!next && !save.isPending ? onClose() : undefined)}>
      <DialogContent className="max-h-[calc(100vh-4rem)] overflow-y-auto sm:max-w-2xl">
        <DialogTitle>{existing ? 'Edit shipment' : 'New shipment'}</DialogTitle>
        <form
          className="mt-2 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSave) save.mutate();
          }}
        >
          <div className="grid gap-4 sm:grid-cols-[160px_1fr]">
            <Field guide="shipment-carrier" label="Carrier" htmlFor="shipment-carrier" required>
              <SelectInput
                id="shipment-carrier"
                value={carrier}
                onChange={(event) => setCarrier(event.target.value)}
              >
                {carrierOptions.map((option) => (
                  <option key={option} value={option}>
                    {carrierLabel(option)}
                  </option>
                ))}
              </SelectInput>
            </Field>
            <Field guide="shipment-tracking" label="Tracking number" htmlFor="shipment-tracking" required={!FREIGHT_CARRIERS.has(carrier)}>
              <TextInput
                id="shipment-tracking"
                value={trackingNumber}
                placeholder="Carrier tracking #"
                onChange={(event) => setTrackingNumber(event.target.value)}
              />
            </Field>
          </div>

          {statusIsManual(carrier) ? (
            <ErrorNotice
              inline
              tone="warning"
              message={`Shippo does not track ${carrierLabel(carrier)}. The status you set here is the status until someone changes it; the tracking link opens the carrier's own page.`}
            />
          ) : null}

          {FREIGHT_CARRIERS.has(carrier) ? (
            <Field label="PRO number" htmlFor="shipment-pro">
              <TextInput
                id="shipment-pro"
                value={proNumber}
                placeholder="Freight PRO #, e.g. 646-318072"
                onChange={(event) => setProNumber(event.target.value)}
              />
            </Field>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-[120px_90px_90px_1fr]">
            <Field guide="shipment-pieces" label="Pieces" htmlFor="shipment-piece-type">
              <SelectInput id="shipment-piece-type" value={pieceType} onChange={(event) => setPieceType(event.target.value as 'box' | 'pallet')}>
                <option value="box">Boxes</option>
                <option value="pallet">Pallets</option>
              </SelectInput>
            </Field>
            <Field label="This is #" htmlFor="shipment-piece-count">
              <TextInput id="shipment-piece-count" type="number" min={0} value={pieceCount} placeholder="1" onChange={(event) => setPieceCount(event.target.value)} />
            </Field>
            <Field label="of" htmlFor="shipment-piece-of">
              <TextInput id="shipment-piece-of" type="number" min={0} value={pieceOf} placeholder="3" onChange={(event) => setPieceOf(event.target.value)} />
            </Field>
            {statusIsManual(carrier) ? (
              <Field label="Status (set by hand)" htmlFor="shipment-status">
                <SelectInput id="shipment-status" value={status} onChange={(event) => setStatus(event.target.value)}>
                  {Object.entries(SHIPMENT_STATUSES).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </SelectInput>
              </Field>
            ) : (
              <div className="self-end pb-2 text-portal-micro text-portal-muted">Status comes from the carrier feed.</div>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field guide="shipment-vendor" label="Vendor" htmlFor="shipment-vendor" required>
              <TextInput
                id="shipment-vendor"
                value={vendor}
                placeholder="Who is shipping this, e.g. BCE - Logan"
                onChange={(event) => setVendor(event.target.value)}
              />
            </Field>
            <Field guide="shipment-arrival" label="Expected arrival" htmlFor="shipment-arrival">
              <DatePicker
                id="shipment-arrival"
                value={arrival}
                placeholder="When should it arrive?"
                onChange={setArrival}
              />
            </Field>
          </div>

          <Field
            label="Box label"
            htmlFor="shipment-label-text"
            hint="Exactly what the venue wants written on the label, one line per line. It shows on the shipment and in the email to the client."
          >
            <TextArea
              id="shipment-label-text"
              rows={3}
              value={labelText}
              placeholder={'Jane Rivera / Acme Corp\nc/o Harbor Hotel, receiving dock'}
              onChange={(event) => setLabelText(event.target.value)}
            />
          </Field>

          <div>
            <span className="mb-1.5 block text-portal-micro font-extrabold tracking-[1px] text-portal-muted uppercase">
              Items
            </span>
            <div data-guide="shipment-items" className="space-y-2">
              {items.map((item, index) => (
                // Index keys are safe here: rows are only appended/removed by
                // the buttons below, and the inputs are controlled.
                // eslint-disable-next-line react/no-array-index-key
                <div key={index} className="flex items-center gap-2">
                  {packingLines.length > 0 ? (
                    <div className="w-[220px] shrink-0">
                      <label htmlFor={`shipment-item-${index}-line`} className="sr-only">
                        Item {index + 1} from materials
                      </label>
                      <Combobox
                        id={`shipment-item-${index}-line`}
                        options={packingLines.map((line) => ({ value: line._id, label: `${line.name} (${line.quantity}${line.unit ? ` ${line.unit}` : ''})` }))}
                        value={item.packingLineId}
                        onChange={(value) => linkItem(index, value)}
                        placeholder="From materials"
                        noneLabel="Typed by hand"
                      />
                    </div>
                  ) : null}
                  <TextInput
                    aria-label={`Item ${index + 1} name`}
                    value={item.name}
                    placeholder="What is in this shipment?"
                    disabled={Boolean(item.packingLineId)}
                    onChange={(event) => setItem(index, { name: event.target.value })}
                    className="flex-1"
                  />
                  <TextInput
                    aria-label={`Item ${index + 1} quantity`}
                    type="number"
                    min={0}
                    value={item.quantity}
                    placeholder="Qty"
                    onChange={(event) => setItem(index, { quantity: event.target.value })}
                    className="w-[90px]"
                  />
                  <button
                    type="button"
                    aria-label={`Remove item ${index + 1}`}
                    onClick={() => setItems((rows) => rows.filter((_, i) => i !== index))}
                    className="grid size-9 shrink-0 place-items-center rounded-portal-button text-portal-muted transition-colors hover:bg-portal-bg hover:text-rose-600"
                  >
                    <XIcon aria-hidden className="size-4" />
                  </button>
                </div>
              ))}
              <Button guide="shipment-add-item"
                type="button"
                variant="secondary"
                onClick={() => setItems((rows) => [...rows, { packingLineId: '', name: '', quantity: '1' }])}
              >
                <PlusIcon aria-hidden className="size-4" /> Add item
              </Button>
            </div>
          </div>

          {returnLabelsOn ? (
            <Field
              label="Return label"
              htmlFor="shipment-return-label"
              hint="For a label sent to the venue or client. It raises no tracking alerts, stays off the client's portal, and Production is told if no carrier scans it within a week of the event."
            >
              <label className="mt-1.5 flex items-center gap-2 text-portal-small">
                <Checkbox
                  id="shipment-return-label"
                  checked={returnLabel}
                  onCheckedChange={(state) => setReturnLabel(state === true)}
                />
                This is a prepaid label for shipping the materials back
              </label>
            </Field>
          ) : null}

          {/* A return label never reaches the client's portal, so the switch has nothing to say. */}
          {returnLabelsOn && returnLabel ? null : (
            <label className="flex items-center gap-2 text-portal-small">
              <Checkbox
                checked={viewableByClient}
                onCheckedChange={(state) => setViewableByClient(state === true)}
              />
              Viewable by the client in their portal
            </label>
          )}

          {save.isError ? (
            <ErrorNotice inline title="Could not save" message={errorMessage(save.error)} />
          ) : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSave}>
              {save.isPending ? 'Saving…' : existing ? 'Save changes' : 'Create shipment'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Attachments ────────────────────────────────────────────────────────────

/** Sortable columns of the attachments table; keys are row fields. */
const ATTACHMENT_SORT_FIELDS = {
  name: 'name',
  type: 'type',
  uploaded: 'createdAt',
  by: 'uploadedByName',
} as const;
const ATTACHMENT_SORT_KEYS = Object.values(ATTACHMENT_SORT_FIELDS);
const DEFAULT_ATTACHMENT_SORT: DataTableSort = { key: ATTACHMENT_SORT_FIELDS.uploaded, direction: 'desc' };

/** A table row: the attachment plus the flattened fields the table sorts and searches on. */
interface AttachmentRow extends EventAttachment {
  readonly uploadedByName: string;
}

function uploadedByName(attachment: EventAttachment): string {
  const by = attachment.uploadedBy;
  if (by && typeof by !== 'string') return `${by.firstName ?? ''} ${by.lastName ?? ''}`.trim();
  // Portal uploads carry a name and which side of the table it came from.
  if (attachment.uploadedByName) {
    return attachment.uploadedBySide === 'CLIENT'
      ? `${attachment.uploadedByName} (client)`
      : attachment.uploadedByName;
  }
  return '';
}

/** The role a document plays on the portal's CSR card, when it has one. */
const DOCUMENT_ROLE_LABELS: Readonly<Record<string, string>> = {
  CSR_PHOTO: 'CSR photo',
  CSR_TAX_LETTER: 'CSR tax letter',
  CSR_ACKNOWLEDGEMENT: 'CSR acknowledgement',
};

function documentRole(attachment: EventAttachment): string | null {
  if (attachment.documentType && DOCUMENT_ROLE_LABELS[attachment.documentType]) {
    return DOCUMENT_ROLE_LABELS[attachment.documentType] ?? null;
  }
  return attachment.tags?.includes('csr') ? 'CSR' : null;
}

function uploadedOn(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** One chosen file's progress through the upload queue. */
const UPLOAD_STATE = {
  queued: 'queued',
  uploading: 'uploading',
  done: 'done',
  failed: 'failed',
} as const;
type UploadState = (typeof UPLOAD_STATE)[keyof typeof UPLOAD_STATE];

const UPLOAD_PILL: Readonly<Record<UploadState, { tone: PillTone; label: string }>> = {
  queued: { tone: 'neutral', label: 'Ready' },
  uploading: { tone: 'info', label: 'Uploading' },
  done: { tone: 'success', label: 'Uploaded' },
  failed: { tone: 'danger', label: 'Failed' },
};

/** A file waiting in the add dialog, with the facts the user gives it. */
interface PendingUpload {
  readonly id: string;
  readonly file: File;
  readonly name: string;
  readonly description: string;
  readonly contractor: boolean;
  readonly client: boolean;
  readonly state: UploadState;
  readonly message?: string;
}

/** Internal always sees a file; the two toggles decide the rest. */
function audienceFor(input: { readonly contractor: boolean; readonly client: boolean }): AttachmentAudience[] {
  return [
    ATTACHMENT_AUDIENCE.internal,
    ...(input.contractor ? [ATTACHMENT_AUDIENCE.contractor] : []),
    ...(input.client ? [ATTACHMENT_AUDIENCE.client] : []),
  ];
}

/** "roster.xlsx" → "roster": the default title is the file name without its extension. */
function titleFromFilename(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot > 0 ? filename.slice(0, dot) : filename;
}

/**
 * Files attached to the event. Choosing a file opens a dialog for its
 * title, an optional description and its audience before anything is
 * sent (one file at a time, on purpose). Client sharing is a switch on the
 * row; everything else about a row lives behind its actions menu.
 *
 * @param props - The event id.
 * @returns The attachments tab.
 */
export function AttachmentsTab({ eventId }: { readonly eventId: string }): React.ReactElement {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [pending, setPending] = useState<PendingUpload | null>(null);
  const [editing, setEditing] = useState<EventAttachment | null>(null);
  const [removing, setRemoving] = useState<EventAttachment | null>(null);
  const [search, setSearch] = useState('');
  const { sort, onSort } = useTableSort(ATTACHMENT_SORT_KEYS, DEFAULT_ATTACHMENT_SORT);
  const attachments = useQuery({
    queryKey: ['event-attachments', eventId],
    queryFn: ({ signal }) => listEventAttachments(eventId, signal),
  });

  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['event-attachments', eventId] });

  const share = useMutation({
    mutationFn: (input: { attachment: EventAttachment; client: boolean }) =>
      updateEventAttachment(input.attachment._id, {
        viewableBy: audienceFor({
          contractor: input.attachment.viewableBy?.includes(ATTACHMENT_AUDIENCE.contractor) ?? true,
          client: input.client,
        }),
      }),
    onSuccess: async (_result, input) => {
      await refresh();
      toast.success(input.client ? `${input.attachment.name} is now shared with the client` : `${input.attachment.name} is no longer shared with the client`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: (attachment: EventAttachment) => deleteEventAttachment(attachment._id),
    onSuccess: async (_result, attachment) => {
      setRemoving(null);
      await refresh();
      toast.success(`${attachment.name} removed`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  function pickFiles(): void {
    fileInputRef.current?.click();
  }
  function onFileChosen(event: React.ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    // Reset so choosing the same file again still fires a change event.
    event.target.value = '';
    if (!file) return;
    const rejection = attachmentRejection(file);
    setPending({
      id: String(Date.now()),
      file,
      name: titleFromFilename(file.name),
      description: '',
      contractor: true,
      client: false,
      state: rejection ? UPLOAD_STATE.failed : UPLOAD_STATE.queued,
      ...(rejection ? { message: rejection } : {}),
    });
  }

  if (attachments.isPending) return <LoadingState label="Loading attachments…" />;
  if (attachments.isError) {
    return <ErrorNotice title="Attachments unavailable" message={errorMessage(attachments.error)} />;
  }

  const rows: AttachmentRow[] = sortRows(
    filterRows(
      attachments.data.map((attachment) => ({ ...attachment, uploadedByName: uploadedByName(attachment) })),
      search,
      ['name', 'description', 'uploadedByName'],
    ),
    sort,
  );

  const uploadButton = (
    <Button onClick={pickFiles}>
      <PaperclipIcon aria-hidden className="size-4" />
      Upload a file
    </Button>
  );

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept={ATTACHMENT_ACCEPT.join(',')}
        className="hidden"
        aria-label="Choose a file to attach"
        onChange={onFileChosen}
      />
      {attachments.data.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<PaperclipIcon aria-hidden className="size-6" />}
            title="No attachments yet"
            message="Contracts, layouts, rosters and any other files for this event live here."
            action={uploadButton}
          />
        </Panel>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <SearchInput
              label="Search attachments"
              placeholder="Title, description or who uploaded it"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="w-full max-w-sm"
            />
            {uploadButton}
          </div>
          {rows.length === 0 ? (
            <Panel>
              <EmptyState message="No attachments match that search." />
            </Panel>
          ) : (
            <DataTable
              headers={[
                { label: 'Name', sortKey: ATTACHMENT_SORT_FIELDS.name },
                { label: 'Type', sortKey: ATTACHMENT_SORT_FIELDS.type },
                { label: 'Uploaded', sortKey: ATTACHMENT_SORT_FIELDS.uploaded },
                { label: 'By', sortKey: ATTACHMENT_SORT_FIELDS.by },
                'Client',
                'Actions',
              ]}
              visuallyHiddenHeaders={[5]}
              sort={sort}
              onSort={onSort}
              minWidth={720}
            >
              {rows.map((attachment) => {
                const sharedWithClient = attachment.viewableBy?.includes(ATTACHMENT_AUDIENCE.client) ?? false;
                const busy = share.isPending && share.variables?.attachment._id === attachment._id;
                return (
                  <tr key={attachment._id} className="border-t border-portal-line align-middle">
                    <td className="p-3 text-portal-small">
                      <span className="flex items-center gap-2">
                        <Link
                          to={`/attachments/${attachment._id}`}
                          className="block truncate text-portal-small font-bold text-brand hover:underline"
                        >
                          {attachment.name}
                        </Link>
                        {documentRole(attachment) ? <Pill tone="brand">{documentRole(attachment)}</Pill> : null}
                      </span>
                      {attachment.description ? (
                        <span className="block truncate text-portal-micro text-portal-muted">{attachment.description}</span>
                      ) : null}
                    </td>
                    <td className="p-3 text-portal-small whitespace-nowrap">
                      {attachment.type ? <Pill tone="neutral">{attachment.type}</Pill> : null}
                    </td>
                    <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{uploadedOn(attachment.createdAt)}</td>
                    <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{attachment.uploadedByName || '—'}</td>
                    <td className="p-3 text-portal-small">
                      <input
                        type="checkbox"
                        checked={sharedWithClient}
                        disabled={busy}
                        aria-label={`Share ${attachment.name} with the client`}
                        onChange={(event) => share.mutate({ attachment, client: event.target.checked })}
                      />
                    </td>
                    <td className="p-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            aria-label={`Actions for ${attachment.name}`}
                            className="grid size-7 place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-portal-ink"
                          >
                            <EllipsisIcon aria-hidden className="size-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => setEditing(attachment)}>
                            <PencilIcon aria-hidden className="size-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <a href={`/api/v3/attachments/${attachment._id}/view?download=1`} download>
                              <DownloadIcon aria-hidden className="size-4" />
                              Download
                            </a>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <a href={`/api/v3/attachments/${attachment._id}/view`} target="_blank" rel="noreferrer">
                              <ExternalLinkIcon aria-hidden className="size-4" />
                              Open in a new tab
                            </a>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem variant="destructive" onSelect={() => setRemoving(attachment)}>
                            <XIcon aria-hidden className="size-4" />
                            Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </DataTable>
          )}
        </>
      )}

      {pending ? (
        <AddAttachmentDialog
          eventId={eventId}
          initial={pending}
          onClose={() => setPending(null)}
          onUploaded={async () => {
            await refresh();
            toast.success(`${pending.name.trim() || pending.file.name} attached`);
          }}
        />
      ) : null}
      <ConfirmDialog
        open={removing !== null}
        tone="danger"
        title="Remove this attachment?"
        message={removing ? `"${removing.name}" and its file will be deleted. This cannot be undone.` : ''}
        confirmLabel="Remove"
        pending={remove.isPending}
        onConfirm={() => {
          if (removing) remove.mutate(removing);
        }}
        onCancel={() => setRemoving(null)}
      />
      {editing ? (
        <EditAttachmentDialog
          attachment={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refresh();
            toast.success('Attachment updated');
          }}
        />
      ) : null}
    </div>
  );
}

/** The audience switches shared by the add and edit dialogs. */
function AudienceToggles({
  contractor,
  client,
  disabled,
  onChange,
}: {
  readonly contractor: boolean;
  readonly client: boolean;
  readonly disabled?: boolean;
  readonly onChange: (next: { contractor: boolean; client: boolean }) => void;
}): React.ReactElement {
  return (
    <div className="flex flex-wrap items-center gap-4 text-portal-small">
      <span className="text-portal-micro text-portal-muted">Internal always</span>
      <label className="flex items-center gap-1.5">
        <input type="checkbox" checked={contractor} disabled={disabled} onChange={(e) => onChange({ contractor: e.target.checked, client })} />
        Contractors
      </label>
      <label className="flex items-center gap-1.5">
        <input type="checkbox" checked={client} disabled={disabled} onChange={(e) => onChange({ contractor, client: e.target.checked })} />
        Client
      </label>
    </div>
  );
}

/**
 * One chosen file, with its title, an optional description and its
 * audience. Closes itself once the upload is in; a failure keeps it open
 * with the reason so the person can fix and retry.
 */
function AddAttachmentDialog({
  eventId,
  initial,
  onClose,
  onUploaded,
}: {
  readonly eventId: string;
  readonly initial: PendingUpload;
  readonly onClose: () => void;
  readonly onUploaded: () => Promise<void>;
}): React.ReactElement {
  const [row, setRow] = useState<PendingUpload>(initial);
  const patch = (change: Partial<PendingUpload>): void => setRow((current) => ({ ...current, ...change }));
  const rejected = Boolean(attachmentRejection(row.file));

  const upload = useMutation({
    mutationFn: async () => {
      patch({ state: UPLOAD_STATE.uploading, message: undefined });
      await uploadEventAttachment(eventId, row.file, {
        name: row.name.trim() || row.file.name,
        description: row.description,
        viewableBy: audienceFor(row),
      });
    },
    onSuccess: async () => {
      patch({ state: UPLOAD_STATE.done });
      await onUploaded();
      onClose();
    },
    onError: (err) => patch({ state: UPLOAD_STATE.failed, message: errorMessage(err) }),
  });

  return (
    <Dialog open onOpenChange={(next) => (!next && !upload.isPending ? onClose() : undefined)}>
      <DialogContent className="sm:max-w-lg">
        <DialogTitle>Add attachment</DialogTitle>
        <form
          className="mt-2 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!upload.isPending && !rejected) upload.mutate();
          }}
        >
          <div className="flex items-center gap-3">
            <FileTextIcon aria-hidden className="size-4 shrink-0 text-portal-muted" />
            <span className="min-w-0 flex-1 truncate text-portal-micro text-portal-muted">
              {row.file.name} · {Math.max(1, Math.round(row.file.size / 1024))} KB
            </span>
            <Pill tone={UPLOAD_PILL[row.state].tone}>{UPLOAD_PILL[row.state].label}</Pill>
          </div>
          {row.message ? <p className="text-portal-micro text-rose-700">{row.message}</p> : null}
          {rejected ? null : (
            <>
              <Field label="Title" htmlFor="att-add-name">
                <TextInput id="att-add-name" value={row.name} disabled={upload.isPending} onChange={(e) => patch({ name: e.target.value })} />
              </Field>
              <Field label="Description" htmlFor="att-add-desc">
                <TextInput id="att-add-desc" value={row.description} disabled={upload.isPending} placeholder="What this is, for whoever opens it" onChange={(e) => patch({ description: e.target.value })} />
              </Field>
              <AudienceToggles contractor={row.contractor} client={row.client} disabled={upload.isPending} onChange={(next) => patch(next)} />
            </>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={upload.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={upload.isPending || rejected}>
              {upload.isPending ? 'Uploading…' : 'Upload'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Title, description and audience of one existing attachment. */
function EditAttachmentDialog({
  attachment,
  onClose,
  onSaved,
}: {
  readonly attachment: EventAttachment;
  readonly onClose: () => void;
  readonly onSaved: () => Promise<void>;
}): React.ReactElement {
  const [name, setName] = useState(attachment.name);
  const [description, setDescription] = useState(attachment.description ?? '');
  const [audience, setAudience] = useState({
    contractor: attachment.viewableBy?.includes(ATTACHMENT_AUDIENCE.contractor) ?? true,
    client: attachment.viewableBy?.includes(ATTACHMENT_AUDIENCE.client) ?? false,
  });
  const save = useMutation({
    mutationFn: () =>
      updateEventAttachment(attachment._id, {
        name: name.trim() || attachment.name,
        description: description.trim(),
        viewableBy: audienceFor(audience),
      }),
    onSuccess: onSaved,
  });

  return (
    <Dialog open onOpenChange={(next) => (!next && !save.isPending ? onClose() : undefined)}>
      <DialogContent className="sm:max-w-lg">
        <DialogTitle>Edit attachment</DialogTitle>
        <form
          className="mt-2 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!save.isPending) save.mutate();
          }}
        >
          <Field label="Title" htmlFor="att-edit-name">
            <TextInput id="att-edit-name" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Description" htmlFor="att-edit-desc">
            <TextInput id="att-edit-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <AudienceToggles contractor={audience.contractor} client={audience.client} onChange={setAudience} />
          {save.isError ? <ErrorNotice inline title="Could not save" message={errorMessage(save.error)} /> : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Recap ──────────────────────────────────────────────────────────────────

/** The follow-up a LEGACY recap nests under its triggering answer. */
interface NestedRecapConditional {
  readonly question?: string;
  readonly answer?: unknown;
}

/**
 * The triggered nested follow-up on a legacy recap item, if any.
 *
 * Two storage shapes exist: recaps filed in the classic portal nest the
 * follow-up's answer inside `conditionals[<answer>]` (or `.any`); recaps
 * filed here store it as a separate `<name>__detail` item. The separate item
 * renders as its own row — this recovers the nested one.
 */
function nestedConditional(answer: RecapAnswer): NestedRecapConditional | null {
  const table = answer.conditionals as
    | Record<string, NestedRecapConditional | undefined>
    | undefined;
  if (!table) return null;
  const hit = table[String(answer.answer ?? '')] ?? table['any'];
  if (!hit || typeof hit !== 'object') return null;
  // Only a follow-up that was actually answered (or at least asked with a
  // question) is content; a bare template entry is not.
  return hit.question || hit.answer ? hit : null;
}

/**
 * Renders one recap answer in plain terms — including the legacy nested
 * follow-up comment, which is where the classic portal kept the details.
 *
 * @param props - The answer.
 * @returns The row.
 */
export function RecapAnswerRow({ answer }: { readonly answer: RecapAnswer }): React.ReactElement {
  const value = answer.answer;
  // Answers are stored as typed ('Yes' | 'No' | 'N/A'); compare case-folded.
  const normalized = String(value ?? '').trim().toLowerCase();
  const conditional = nestedConditional(answer);
  return (
    <div className="border-b border-portal-line px-4 py-3 last:border-b-0">
      <p className="text-portal-small font-bold">{answer.question}</p>
      {answer.type === 'yes_no_na' ? (
        <Pill
          tone={normalized === 'yes' ? 'success' : normalized === 'no' ? 'danger' : 'neutral'}
          className="mt-1.5"
        >
          {String(value ?? 'n/a').toUpperCase()}
        </Pill>
      ) : (
        <p className="mt-1.5 text-portal-small whitespace-pre-wrap">{String(value ?? '—')}</p>
      )}
      {conditional ? (
        <div className="mt-2.5">
          <p className="text-portal-small font-bold">{conditional.question ?? 'Details'}</p>
          <p className="mt-1 text-portal-small whitespace-pre-wrap">
            {String(conditional.answer ?? '').trim() || '—'}
          </p>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The facilitator's post-event recap for this event, if one exists.
 *
 * @param props - The event id.
 * @returns The recap tab.
 */
/** The facilitator name from a recap's populated facilitator ref. */
function recapAuthorName(facilitator: EventRecap['facilitator']): string {
  if (typeof facilitator !== 'object' || facilitator === null) return 'Facilitator';
  return (
    [facilitator.firstName, facilitator.lastName].filter(Boolean).join(' ') ||
    facilitator.email ||
    'Facilitator'
  );
}

export function RecapTab({
  eventId,
  offersClientFeedback = false,
}: {
  readonly eventId: string;
  /** True when the event delivers a portal booking, so the form asks for feedback for the client. */
  readonly offersClientFeedback?: boolean;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  // The recap whose feedback staff are correcting, in the side panel.
  const [correcting, setCorrecting] = useState<EventRecap | null>(null);

  const me = useQuery({ queryKey: ['staff-me'], queryFn: ({ signal }) => readMyIdentity(signal) });
  const recaps = useQuery({
    queryKey: ['event-recaps', eventId],
    queryFn: ({ signal }) => listRecaps({ eventId }, signal),
  });

  if (recaps.isPending || me.isPending) return <LoadingState label="Loading recap…" />;
  if (recaps.isError) {
    return <ErrorNotice title="Recap unavailable" message={errorMessage(recaps.error)} />;
  }

  const myId = me.data?.id;
  const list = recaps.data.recaps;
  const mine = list.find(
    (r) => typeof r.facilitator === 'object' && r.facilitator?._id === myId,
  );
  const others = list.filter((r) => r !== mine);

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['event-recaps', eventId] });
    setEditing(false);
    setCorrecting(null);
  }
  // Staff correct a facilitator's feedback for the client after it publishes.
  const correctable = (recap: EventRecap): boolean =>
    editsAnyRecap(me.data?.role) && Boolean(recap.clientFeedback?.trim());

  return (
    <div className="mt-6 space-y-6">
      {/* Your recap: the form when editing, otherwise your submitted recap or
          a prompt to submit one. */}
      {editing ? (
        <RecapForm
          eventId={eventId}
          facilitatorId={myId ?? ''}
          existing={mine}
          offersClientFeedback={offersClientFeedback}
          onDone={refresh}
          onCancel={() => setEditing(false)}
        />
      ) : mine ? (
        <div>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-portal-section font-bold tracking-[-0.4px]">Your recap</h3>
            <Button variant="secondary" onClick={() => setEditing(true)}>
              Edit
            </Button>
          </div>
          <RecapCard recap={mine} />
        </div>
      ) : (
        <Panel className="p-6 text-center">
          <FileTextIcon aria-hidden className="mx-auto size-6 text-portal-muted" />
          <p className="mt-2 text-portal-small font-bold">
            You have not submitted a recap for this event yet.
          </p>
          <p className="mt-1 text-portal-micro text-portal-muted">
            Multiple facilitators can each submit their own.
          </p>
          <Button className="mt-4" onClick={() => setEditing(true)} disabled={!myId}>
            <PlusIcon aria-hidden className="size-4" /> Submit event recap
          </Button>
        </Panel>
      )}

      {/* Everyone else's recaps, read-only. */}
      {others.length > 0 ? (
        <div>
          <h3 className="mb-2 text-portal-section font-bold tracking-[-0.4px]">
            {mine ? 'Other recaps' : 'Submitted recaps'}
          </h3>
          <div className="space-y-3">
            {others.map((recap) => (
              <RecapCard
                key={recap._id}
                recap={recap}
                {...(correctable(recap) ? { onEditFeedback: () => setCorrecting(recap) } : {})}
              />
            ))}
          </div>
        </div>
      ) : null}

      <ClientFeedbackDrawer recap={correcting} onClose={() => setCorrecting(null)} onSaved={refresh} />
    </div>
  );
}

/**
 * A recap's feedback for the client, marked as what the client reads.
 *
 * @param props - The feedback, and an edit action for staff.
 * @returns The block.
 */
export function ClientFeedbackRow({
  feedback,
  onEdit,
}: {
  readonly feedback: string;
  readonly onEdit?: (() => void) | undefined;
}): React.ReactElement {
  return (
    <div data-guide="recap-client-feedback" className="border-b border-portal-line bg-portal-sky px-4 py-3 last:border-b-0">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-portal-small font-bold">Feedback for the client</p>
          <p className="text-portal-micro text-portal-muted">Shown on the client's portal, on this event's page.</p>
        </div>
        {onEdit ? (
          <Button guide="recap-edit-feedback" variant="secondary" onClick={onEdit}>
            <PencilIcon aria-hidden className="size-4" /> Edit feedback
          </Button>
        ) : null}
      </div>
      <p className="mt-2 text-portal-small whitespace-pre-wrap">{feedback}</p>
    </div>
  );
}

/** One submitted recap, read-only. */
function RecapCard({
  recap,
  onEditFeedback,
}: {
  readonly recap: EventRecap;
  /** Opens the side panel where staff correct the feedback for the client. */
  readonly onEditFeedback?: () => void;
}): React.ReactElement {
  const feedback = recap.clientFeedback?.trim();
  return (
    <Panel>
      <div className="border-b border-portal-line px-4 py-3">
        <h3 className="text-portal-card-title font-bold">{recapAuthorName(recap.facilitator)}</h3>
        {recap.createdAt === undefined ? null : (
          <p className="text-portal-micro text-portal-muted">
            Submitted {new Date(recap.createdAt).toLocaleDateString()}
          </p>
        )}
      </div>
      {feedback ? <ClientFeedbackRow feedback={feedback} onEdit={onEditFeedback} /> : null}
      {recap.recap.map((answer) => (
        <RecapAnswerRow key={answer.name} answer={answer} />
      ))}
    </Panel>
  );
}

/**
 * Where staff correct a facilitator's feedback for the client once it has
 * published. Saving blank takes it off the client's portal.
 *
 * @param props - The recap being corrected (null when closed), and callbacks.
 * @returns The drawer.
 */
function ClientFeedbackDrawer({
  recap,
  onClose,
  onSaved,
}: {
  readonly recap: EventRecap | null;
  readonly onClose: () => void;
  readonly onSaved: () => Promise<void>;
}): React.ReactElement {
  // Keyed by the recap, so opening another one starts from its own text.
  return (
    <Drawer
      open={recap !== null}
      title="Edit feedback for the client"
      {...(recap ? { subtitle: `Written by ${recapAuthorName(recap.facilitator)}` } : {})}
      onClose={onClose}
    >
      {recap ? <ClientFeedbackForm key={recap._id} recap={recap} onCancel={onClose} onSaved={onSaved} /> : null}
    </Drawer>
  );
}

function ClientFeedbackForm({
  recap,
  onCancel,
  onSaved,
}: {
  readonly recap: EventRecap;
  readonly onCancel: () => void;
  readonly onSaved: () => Promise<void>;
}): React.ReactElement {
  const [text, setText] = useState(recap.clientFeedback ?? '');
  const save = useMutation({
    mutationFn: () => updateRecap(recap._id, { clientFeedback: text.trim() === '' ? null : text }),
    onSuccess: async () => {
      toast.success('Feedback saved');
      await onSaved();
    },
  });
  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <Field
        guide="client-feedback-text"
        label="Feedback for the client"
        htmlFor="client-feedback-edit"
        hint="The client reads this on their portal. Leave it blank to take it down."
      >
        <TextArea
          id="client-feedback-edit"
          rows={8}
          maxLength={CLIENT_FEEDBACK_MAX_LENGTH}
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
      </Field>
      {save.isError ? <ErrorNotice inline title="Feedback not saved" message={errorMessage(save.error)} /> : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={save.isPending}>
          Cancel
        </Button>
        <Button guide="client-feedback-save" type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save feedback'}
        </Button>
      </div>
    </form>
  );
}

/**
 * The recap questionnaire — submit a new recap or edit your existing one.
 *
 * Renders the standard question set: yes/no/N-A questions reveal a follow-up
 * comment when their answer triggers a conditional, free-text questions get a
 * textarea. Required questions block submission until answered.
 *
 * @param props - Event + facilitator ids, an optional existing recap to edit.
 * @returns The recap form.
 */
function RecapForm({
  eventId,
  facilitatorId,
  existing,
  offersClientFeedback,
  onDone,
  onCancel,
}: {
  readonly eventId: string;
  readonly facilitatorId: string;
  readonly existing: EventRecap | undefined;
  /** Asks for feedback for the client: only on an event attached to a portal booking. */
  readonly offersClientFeedback: boolean;
  readonly onDone: () => Promise<void>;
  readonly onCancel: () => void;
}): React.ReactElement {
  // Seed the form from an existing recap: main answers by name, and each
  // conditional follow-up into details. Follow-ups come in two shapes: recaps
  // filed here store them as separate "<name>__detail" items; recaps filed in
  // the classic portal nest them inside conditionals[<answer>].answer — read
  // both, or editing a legacy recap would silently drop its comments.
  const seed = (): { answers: Record<string, string>; details: Record<string, string> } => {
    const answers: Record<string, string> = {};
    const details: Record<string, string> = {};
    for (const item of existing?.recap ?? []) {
      if (item.name?.endsWith(RECAP_DETAIL_SUFFIX)) {
        details[item.name.slice(0, -RECAP_DETAIL_SUFFIX.length)] = String(item.answer ?? '');
      } else if (item.name) {
        answers[item.name] = String(item.answer ?? '');
        const table = item.conditionals as
          | Record<string, { answer?: unknown } | undefined>
          | undefined;
        const nested = table?.[String(item.answer ?? '')] ?? table?.['any'];
        if (nested?.answer !== undefined && details[item.name] === undefined) {
          details[item.name] = String(nested.answer ?? '');
        }
      }
    }
    return { answers, details };
  };
  const [{ answers, details }, setState] = useState(seed);
  const [clientFeedback, setClientFeedback] = useState(existing?.clientFeedback ?? '');
  const [showErrors, setShowErrors] = useState(false);

  function setAnswer(name: string, value: string): void {
    setState((prev) => ({ ...prev, answers: { ...prev.answers, [name]: value } }));
  }
  function setDetail(name: string, value: string): void {
    setState((prev) => ({ ...prev, details: { ...prev.details, [name]: value } }));
  }

  /** Names of questions that are required but still blank. */
  const missing = RECAP_QUESTIONS.filter((q) => {
    const answer = answers[q.name];
    if (!answer) return true;
    const cond = conditionalFor(q, answer);
    return cond && cond.required !== false && !details[q.name]?.trim();
  }).map((q) => q.name);

  const save = useMutation({
    mutationFn: () => {
      // Build the recap array: each question, plus a "<name>__detail" item
      // for any triggered conditional follow-up.
      const items: RecapAnswer[] = [];
      for (const q of RECAP_QUESTIONS) {
        items.push({ name: q.name, question: q.question, answer: answers[q.name] ?? '', type: q.type });
        const cond = conditionalFor(q, answers[q.name]);
        if (cond && details[q.name]?.trim()) {
          items.push({
            name: `${q.name}${RECAP_DETAIL_SUFFIX}`,
            question: cond.question,
            answer: details[q.name],
            type: 'comment',
          });
        }
      }
      // The feedback travels only when the event offers it; the API refuses
      // it on an event with no portal booking.
      const feedback = offersClientFeedback ? { clientFeedback: clientFeedback.trim() === '' ? null : clientFeedback } : {};
      return existing
        ? updateRecap(existing._id, { recap: items, ...feedback })
        : createRecap({ event: eventId, facilitator: facilitatorId, recap: items, ...feedback });
    },
    onSuccess: onDone,
  });

  return (
    <Panel className="p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-portal-section font-bold tracking-[-0.4px]">
          {existing ? 'Edit your recap' : 'Event recap'}
        </h3>
        <button
          type="button"
          onClick={onCancel}
          className="text-portal-micro font-bold text-portal-muted hover:text-portal-ink"
        >
          Cancel
        </button>
      </div>

      <form
        className="space-y-5"
        onSubmit={(event) => {
          event.preventDefault();
          if (missing.length > 0) {
            setShowErrors(true);
            return;
          }
          save.mutate();
        }}
      >
        {RECAP_QUESTIONS.map((q) => {
          const answer = answers[q.name] ?? '';
          const cond = conditionalFor(q, answer);
          const isMissing = showErrors && missing.includes(q.name);
          return (
            <div key={q.name}>
              <p className="text-portal-small font-bold text-portal-ink">
                {q.question}
                <span className="text-rose-500"> *</span>
              </p>
              {q.type === 'yes_no_na' ? (
                <div className="mt-1.5 flex gap-4">
                  {['Yes', 'No', 'N/A'].map((option) => (
                    <label key={option} className="flex cursor-pointer items-center gap-1.5 text-portal-small">
                      <input
                        type="radio"
                        name={q.name}
                        checked={answer === option}
                        onChange={() => setAnswer(q.name, option)}
                        className="accent-brand"
                      />
                      {option}
                    </label>
                  ))}
                </div>
              ) : (
                <textarea
                  value={answer}
                  onChange={(event) => setAnswer(q.name, event.target.value)}
                  rows={3}
                  className="mt-1.5 w-full rounded-portal-button border border-portal-line px-3 py-2 text-portal-small outline-none focus:border-brand"
                />
              )}
              {cond ? (
                <div className="mt-2">
                  <p className="text-portal-micro font-bold text-portal-muted">
                    {cond.question}
                    {cond.required === false ? '' : <span className="text-rose-500"> *</span>}
                  </p>
                  <textarea
                    value={details[q.name] ?? ''}
                    onChange={(event) => setDetail(q.name, event.target.value)}
                    rows={2}
                    className="mt-1 w-full rounded-portal-button border border-portal-line px-3 py-2 text-portal-small outline-none focus:border-brand"
                  />
                </div>
              ) : null}
              {isMissing ? (
                <p className="mt-1 text-portal-micro font-semibold text-rose-600">
                  This question is required.
                </p>
              ) : null}
            </div>
          );
        })}

        {offersClientFeedback ? (
          <Field
            label="Feedback for the client"
            htmlFor="recap-client-feedback"
            hint="This event is on the client's portal. What you write here appears on their event page as soon as you save, so write it for them: what went well and anything worth doing next time."
          >
            <TextArea
              id="recap-client-feedback"
              rows={5}
              maxLength={CLIENT_FEEDBACK_MAX_LENGTH}
              value={clientFeedback}
              onChange={(event) => setClientFeedback(event.target.value)}
            />
          </Field>
        ) : null}

        {save.isError ? (
          <ErrorNotice inline title="Recap not saved" message={errorMessage(save.error)} />
        ) : null}

        <div className="flex items-center gap-2">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : existing ? 'Save recap' : 'Submit recap'}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Panel>
  );
}

// ─── Request payment ─────────────────────────────────────────────────────────

/** The Formstack facilitator invoice form the payment tab embeds. */
const PAYMENT_FORM_URL = 'https://bestcorporateevents.formstack.com/forms/facilitator_invoice_copy';

/**
 * The facilitator's request-for-payment section.
 *
 * Mirrors the legacy flow: payment can only be requested after the caller has
 * submitted their event recap — until then this prompts for the recap. Once a
 * recap exists it embeds the Formstack invoice form pre-filled with the
 * event's contract details and the caller's contact/payee information.
 *
 * @param props - The event id and its loaded detail (for prefill fields).
 * @returns The payment tab.
 */
export function PaymentTab({
  eventId,
  detail,
}: {
  readonly eventId: string;
  readonly detail: EventDetail;
}): React.ReactElement {
  const me = useQuery({ queryKey: ['staff-me'], queryFn: ({ signal }) => readMyIdentity(signal) });
  const recaps = useQuery({
    queryKey: ['event-recaps', eventId],
    queryFn: ({ signal }) => listRecaps({ eventId }, signal),
  });

  if (me.isPending || recaps.isPending) return <LoadingState label="Loading payment form…" />;
  if (recaps.isError) {
    return <ErrorNotice title="Payment form unavailable" message={errorMessage(recaps.error)} />;
  }

  const mine = recaps.data.recaps.find(
    (r) => typeof r.facilitator === 'object' && r.facilitator?._id === me.data?.id,
  );

  if (!mine) {
    return (
      <Panel className="mt-6 p-6 text-center">
        <CreditCardIcon aria-hidden className="mx-auto size-6 text-portal-muted" />
        <p className="mt-2 text-portal-small font-bold">
          Submit your event recap before requesting payment.
        </p>
        <p className="mt-1 text-portal-micro text-portal-muted">
          Payment requests unlock once your recap for this event is in.
        </p>
        <Button className="mt-4" to={`/events/${eventId}?tab=recap`}>
          Go to recap
        </Button>
      </Panel>
    );
  }

  const user = me.data;
  const params = new URLSearchParams();
  const set = (key: string, value: string | undefined): void => {
    if (value) params.set(key, value);
  };
  set('contract_number', detail.contractNumber);
  set('client_name', detail.account?.name ?? detail.accountName);
  set('event_date', formatEventDate(detail, 'medium'));
  set('Name-first', user?.firstName);
  set('Name-last', user?.lastName);
  set('phone', user?.phone);
  set('email', user?.email);
  set('make_check_payable_to', user?.company || [user?.firstName, user?.lastName].filter(Boolean).join(' '));
  set('address-address', user?.address?.street);
  set('address-city', user?.address?.city);
  set('address-state', user?.address?.state);
  set('address-zip', user?.address?.postalCode);

  return (
    <div className="mt-6">
      <Panel className="overflow-hidden">
        <iframe
          src={`${PAYMENT_FORM_URL}?${params.toString()}`}
          title="Facilitator request for payment"
          className="h-[3700px] w-full border-0"
        />
      </Panel>
    </div>
  );
}

// ─── Checklist ───────────────────────────────────────────────────────────────

